import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import LocalDataService from "@/service/LocalDataService";
import ApiAiReport, {
  AiReportBatchResponse,
  AiReportQuarter,
  AiReportQuarterDetailResponse,
} from "@/api/ApiAiReport";

export interface AiReportQuarterState {
  quarterDetail: AiReportQuarterDetailResponse | null;
  isFetching: boolean;
  activeBatch: AiReportBatchResponse | null;
}

@(Module({ dynamic: true, store, name: "ai-report-quarter", namespaced: true }) as unknown as (target: new (...args: unknown[]) => VuexModule) => void)
class AiReportQuarterModule
  extends VuexModule
  implements AiReportQuarterState
{
  quarterDetail: AiReportQuarterDetailResponse | null = null;
  isFetching = false;
  activeBatch: AiReportBatchResponse | null = null;

  @Mutation
  setQuarterDetail(value: AiReportQuarterDetailResponse | null) {
    this.quarterDetail = value;
  }

  @Mutation
  setFetching(value: boolean) {
    this.isFetching = value;
  }

  @Mutation
  setActiveBatch(batch: AiReportBatchResponse | null) {
    this.activeBatch = batch;
  }

  @Action
  async loadQuarter({
    businessYear,
    quarter,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return;
    this.setFetching(true);
    try {
      const response = await ApiAiReport.getQuarterDetail({
        nurseryId,
        businessYear,
        quarter,
      });
      this.setQuarterDetail(response);
    } finally {
      this.setFetching(false);
    }
  }

  @Action
  async bulkGenerate({
    businessYear,
    quarter,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    const batch = await ApiAiReport.bulkGenerate({
      nurseryId,
      businessYear,
      quarter,
    });
    this.setActiveBatch(batch);
    return batch;
  }

  @Action
  async bulkPublish({
    businessYear,
    quarter,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    return ApiAiReport.bulkPublish({ nurseryId, businessYear, quarter });
  }

  @Action
  async pollBatch({ batchId }: { batchId: number }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    const batch = await ApiAiReport.getBatch({ nurseryId, batchId });
    this.setActiveBatch(batch);
    return batch;
  }

  @Action
  async exportCsv({
    businessYear,
    quarter,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    return ApiAiReport.exportCsv({ nurseryId, businessYear, quarter });
  }
}

export const aiReportQuarterModule = getModule(AiReportQuarterModule);
